import { useEffect, useState } from 'react'
import { byTime, describeWhen, loadAppointments, type Appointment } from '@core/appointments'
import { addDays, isoLocal } from '@core/calendar'
import { Card } from '@design/components'

/**
 * Today and tomorrow, on the parents' screen.
 *
 * This exists because of a loop I left half open: the evening before an
 * appointment a parent gets "Morgen: tandarts om 14:00", opens the app, and the
 * appointment was nowhere on it. A notification that leads to a screen which
 * does not mention the thing is worse than no notification — it teaches people
 * that the app does not know what it is talking about.
 *
 * Deliberately **not a calendar**. MemberShell says: resist adding to it, every
 * control here is a reason for a parent to hesitate. So this is information and
 * not a control — nothing to tap, nothing to get wrong, and absent entirely on
 * any day with nothing on it (rule 23).
 *
 * Two days and no further. "What is on today" and "what is on tomorrow" are the
 * questions someone actually has at breakfast; a month is a different question
 * and it belongs on a different screen.
 */
export function NextAppointments({ refreshKey }: { refreshKey?: number }) {
  const [items, setItems] = useState<Appointment[]>([])

  useEffect(() => {
    const today = isoLocal(new Date())
    loadAppointments(today, addDays(today, 1))
      .then((all) => setItems(all.sort(byTime)))
      // Quietly: a missing line here must never take the send button with it.
      .catch(() => setItems([]))
  }, [refreshKey])

  if (items.length === 0) return null

  const today = isoLocal(new Date())

  return (
    <section className="mb-4">
      <h2 className="text-sm text-muted mb-2">Afspraken</h2>
      {items.map((a) => (
        <Card key={a.id} className="mb-2">
          <p className="text-sm text-muted">
            {a.onDate === today ? 'Vandaag' : 'Morgen'} · {describeWhen(a)}
          </p>
          <p className="font-medium text-lg">{a.title}</p>
          {a.location && <p className="text-muted">{a.location}</p>}
          {a.notes && <p className="text-sm text-muted mt-0.5">{a.notes}</p>}
        </Card>
      ))}
    </section>
  )
}
